import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Save, X, ChevronDown, Check, ToggleLeft, ToggleRight, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface InventoryProduct {
  id: string;
  company_id: string;
  sku: string;
  name: string;
  price: number;
  cost: number | null;
  image_url: string | null;
  supplier_barcode: string | null;
  supplier_id: string | null;
  created_at: string;
  updated_at: string;
  group_ids: string[];
  group_names: string[];
  b2b_backorder_override: boolean | null;
  shopify_stock_status: 'in_stock' | 'out_of_stock' | null;
  shopify_status: 'active' | 'draft' | 'archived' | null;
  shopify_status_last_checked: string | null;
  is_active: boolean;
  description: string | null;
}

interface Supplier {
  id: string;
  name: string;
  supplier_number: string;
}

interface ProductGroup {
  id: string;
  parent_id: string | null;
  name: string;
}

type EditableColumn = 'price' | 'cost' | 'supplier_id' | 'supplier_barcode' | 'is_active' | 'description' | 'group_ids';

interface ColumnDef {
  key: EditableColumn;
  label: string;
}

const OPTIONAL_COLUMNS: ColumnDef[] = [
  { key: 'price', label: 'מחיר צרכן' },
  { key: 'cost', label: 'עלות ספק' },
  { key: 'supplier_id', label: 'ספק' },
  { key: 'supplier_barcode', label: 'ברקוד ספק' },
  { key: 'group_ids', label: 'קבוצות' },
  { key: 'is_active', label: 'פעיל/לא פעיל' },
  { key: 'description', label: 'תיאור' },
];

const NON_KEYBOARD_COLS: EditableColumn[] = ['is_active', 'group_ids'];

type CellValue = string | number | boolean | string[] | null;
type DirtyMap = Record<string, Record<string, CellValue>>;
type GroupDirtyMap = Record<string, string[]>;

interface Props {
  products: InventoryProduct[];
  suppliers: Supplier[];
  groups: ProductGroup[];
  onSaved: () => void;
}

function GroupMultiSelect({
  selected,
  groups,
  isDirty,
  onChange,
}: {
  selected: string[];
  groups: ProductGroup[];
  isDirty: boolean;
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selectedNames = groups.filter(g => selected.includes(g.id)).map(g => g.name);

  const toggle = (gid: string) => {
    const next = selected.includes(gid)
      ? selected.filter(id => id !== gid)
      : [...selected, gid];
    onChange(next);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-44 px-2 py-1 text-sm border rounded text-right truncate flex items-center justify-between gap-1 transition-colors
          focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400
          ${isDirty ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}
      >
        <span className="truncate text-gray-700">
          {selectedNames.length === 0 ? '—' : selectedNames.join(', ')}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 min-w-48 max-h-56 overflow-y-auto">
          {groups.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">אין קבוצות</div>
          ) : groups.map(g => {
            const isSelected = selected.includes(g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => toggle(g.id)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-right hover:bg-gray-50 transition-colors
                  ${isSelected ? 'text-amber-700 font-medium' : 'text-gray-700'}`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                  ${isSelected ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </span>
                {g.parent_id ? '\u00A0\u00A0' : ''}{g.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function BulkEditProductsView({ products, suppliers, groups, onSaved }: Props) {
  const [visibleCols, setVisibleCols] = useState<EditableColumn[]>(['price', 'cost']);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [dirty, setDirty] = useState<DirtyMap>({});
  const [dirtyGroups, setDirtyGroups] = useState<GroupDirtyMap>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [errorRows, setErrorRows] = useState<Record<string, string>>({});
  const colPickerRef = useRef<HTMLDivElement>(null);



  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target as Node)) {
        setColPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const allDirtyIds = useMemo(() => {
    const ids = new Set(Object.keys(dirty));
    for (const pid of Object.keys(dirtyGroups)) ids.add(pid);
    return ids;
  }, [dirty, dirtyGroups]);
  const dirtyCount = allDirtyIds.size;

  const getVal = useCallback((product: InventoryProduct, col: EditableColumn): CellValue => {
    const rowDirty = dirty[product.id];
    if (rowDirty && col in rowDirty) return rowDirty[col] as CellValue;
    switch (col) {
      case 'price': return product.price;
      case 'cost': return product.cost;
      case 'supplier_id': return product.supplier_id;
      case 'supplier_barcode': return product.supplier_barcode;
      case 'is_active': return product.is_active;
      case 'description': return product.description;
      default: return null;
    }
  }, [dirty]);

  const setVal = useCallback((productId: string, col: EditableColumn, value: CellValue) => {
    setDirty(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [col]: value },
    }));
    setErrorRows(prev => {
      if (!prev[productId]) return prev;
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  }, []);

  const toggleCol = (col: EditableColumn) => {
    setVisibleCols(prev =>
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
    );
  };

  const getGroupIds = useCallback((product: InventoryProduct): string[] => {
    if (product.id in dirtyGroups) return dirtyGroups[product.id]!;
    return product.group_ids;
  }, [dirtyGroups]);

  const setGroupIdsFor = useCallback((productId: string, origIds: string[], newIds: string[]) => {
    const same = origIds.length === newIds.length && origIds.every(id => newIds.includes(id));
    setDirtyGroups(prev => {
      if (same) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: newIds };
    });
    setErrorRows(prev => {
      if (!prev[productId]) return prev;
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  }, []);

  const discardAll = () => {
    setDirty({});
    setDirtyGroups({});
    setErrorRows({});
  };

  const saveAll = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    const newErrors: Record<string, string> = {};
    let successCount = 0;

    const entries = Object.entries(dirty);
    for (const [productId, changes] of entries) {
      const payload: Record<string, CellValue> = {};
      for (const [col, val] of Object.entries(changes)) {
        if (col === 'price') {
          const n = Number(val);
          if (isNaN(n) || n < 0) { newErrors[productId] = 'מחיר לא תקין'; continue; }
          payload.price = n;
        } else if (col === 'cost') {
          if (val === null || val === '') { payload.cost = null; }
          else {
            const n = Number(val);
            if (isNaN(n) || n < 0) { newErrors[productId] = 'עלות לא תקינה'; continue; }
            payload.cost = n;
          }
        } else if (col === 'supplier_id') {
          payload.supplier_id = val || null;
        } else if (col === 'supplier_barcode') {
          payload.supplier_barcode = (val as string)?.trim() || null;
        } else if (col === 'is_active') {
          payload.is_active = val as boolean;
        } else if (col === 'description') {
          payload.description = (val as string)?.trim() || null;
        }
      }

      if (newErrors[productId]) continue;

      const hasGroupChange = productId in dirtyGroups;
      if (Object.keys(payload).length === 0 && !hasGroupChange) {
        successCount++;
        continue;
      }

      if (Object.keys(payload).length > 0) {
        const { error } = await supabase
          .from('inventory_products')
          .update(payload)
          .eq('id', productId);
        if (error) { newErrors[productId] = error.message; continue; }
      }

      if (hasGroupChange) {
        const newGroupIds = dirtyGroups[productId] ?? [];
        const { error: delErr } = await supabase
          .from('inventory_product_group_relations')
          .delete()
          .eq('product_id', productId);
        if (delErr) { newErrors[productId] = delErr.message; continue; }
        if (newGroupIds.length > 0) {
          const { error: insErr } = await supabase
            .from('inventory_product_group_relations')
            .insert(newGroupIds.map(gid => ({ product_id: productId, group_id: gid })));
          if (insErr) { newErrors[productId] = insErr.message; continue; }
        }
      }

      successCount++;
    }

    for (const [productId, newGroupIds] of Object.entries(dirtyGroups)) {
      if (dirty[productId] || newErrors[productId]) continue;
      const { error: delErr } = await supabase
        .from('inventory_product_group_relations')
        .delete()
        .eq('product_id', productId);
      if (delErr) { newErrors[productId] = delErr.message; continue; }
      if (newGroupIds.length > 0) {
        const { error: insErr } = await supabase
          .from('inventory_product_group_relations')
          .insert(newGroupIds.map(gid => ({ product_id: productId, group_id: gid })));
        if (insErr) { newErrors[productId] = insErr.message; continue; }
      }
      successCount++;
    }

    setErrorRows(newErrors);

    const newDirty = { ...dirty };
    for (const pid of Object.keys(newDirty)) {
      if (!newErrors[pid]) delete newDirty[pid];
    }
    setDirty(newDirty);

    const newDirtyGroups = { ...dirtyGroups };
    for (const pid of Object.keys(newDirtyGroups)) {
      if (!newErrors[pid]) delete newDirtyGroups[pid];
    }
    setDirtyGroups(newDirtyGroups);

    if (successCount > 0) {
      setToast({ msg: `${successCount} מוצרים עודכנו בהצלחה`, ok: true });
      onSaved();
    }
    if (Object.keys(newErrors).length > 0) {
      setToast({ msg: `${Object.keys(newErrors).length} שורות נכשלו`, ok: false });
    }
    setSaving(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, colIdx: number) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      const nextColIdx = e.shiftKey ? colIdx - 1 : colIdx + 1;
      const editableCols = visibleCols.filter(c => !NON_KEYBOARD_COLS.includes(c));

      if (nextColIdx >= 0 && nextColIdx < editableCols.length) {
        const nextId = `cell-${rowIdx}-${editableCols[nextColIdx]}`;
        document.getElementById(nextId)?.focus();
      } else if (!e.shiftKey && nextColIdx >= editableCols.length) {
        const nextRowIdx = rowIdx + 1;
        if (nextRowIdx < products.length) {
          const nextId = `cell-${nextRowIdx}-${editableCols[0]}`;
          document.getElementById(nextId)?.focus();
        }
      } else if (e.shiftKey && nextColIdx < 0) {
        const prevRowIdx = rowIdx - 1;
        if (prevRowIdx >= 0) {
          const nextId = `cell-${prevRowIdx}-${editableCols[editableCols.length - 1]}`;
          document.getElementById(nextId)?.focus();
        }
      }
    }
  };

  const editableColIndex = (col: EditableColumn): number => {
    return visibleCols.filter(c => !NON_KEYBOARD_COLS.includes(c)).indexOf(col);
  };

  return (
    <div className="relative">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 px-4 py-3 rounded-lg text-white z-50 flex items-center gap-2 ${toast.ok ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* Column Picker */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          {products.length} מוצרים בטבלה
          {dirtyCount > 0 && (
            <span className="mr-2 text-amber-600 font-medium">
              &middot; {dirtyCount} שורות שונו
            </span>
          )}
        </div>
        <div className="relative" ref={colPickerRef}>
          <button
            onClick={() => setColPickerOpen(!colPickerOpen)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm
                       hover:bg-gray-50 transition-colors"
          >
            עמודות לעריכה
            <ChevronDown className={`w-4 h-4 transition-transform ${colPickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {colPickerOpen && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-48">
              {OPTIONAL_COLUMNS.map(col => (
                <label
                  key={col.key}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={visibleCols.includes(col.key)}
                    onChange={() => toggleCol(col.key)}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-700 whitespace-nowrap w-8">#</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-700 whitespace-nowrap">מק״ט</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-700 whitespace-nowrap">שם הפריט</th>
              {visibleCols.map(col => (
                <th key={col} className="px-3 py-2.5 text-right font-semibold text-gray-700 whitespace-nowrap">
                  {OPTIONAL_COLUMNS.find(c => c.key === col)?.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={3 + visibleCols.length} className="px-4 py-8 text-center text-gray-500">
                  אין תוצאות
                </td>
              </tr>
            ) : (
              products.map((product, rowIdx) => {
                const rowDirty = !!dirty[product.id] || product.id in dirtyGroups;
                const rowError = errorRows[product.id];
                return (
                  <tr
                    key={product.id}
                    className={`border-b border-gray-100 transition-colors
                      ${rowError ? 'bg-red-50' : rowDirty ? 'bg-amber-50/60' : 'hover:bg-gray-50'}
                      ${!product.is_active && !dirty[product.id]?.is_active ? 'opacity-60' : ''}`}
                  >
                    <td className="px-3 py-1.5 text-gray-400 text-xs">{rowIdx + 1}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-700 text-xs whitespace-nowrap">
                      {product.sku}
                    </td>
                    <td className="px-3 py-1.5 text-gray-900 whitespace-nowrap max-w-48 truncate">
                      {product.name}
                      {rowError && (
                        <span className="mr-2 text-[10px] text-red-600 font-medium">{rowError}</span>
                      )}
                    </td>
                    {visibleCols.map(col => (
                      <td key={col} className="px-1.5 py-1">
                        {col === 'group_ids' && (
                          <GroupMultiSelect
                            selected={getGroupIds(product)}
                            groups={groups}
                            isDirty={product.id in dirtyGroups}
                            onChange={(ids) => setGroupIdsFor(product.id, product.group_ids, ids)}
                          />
                        )}
                        {col === 'price' && (
                          <input
                            id={`cell-${rowIdx}-${col}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={String(getVal(product, col) ?? '')}
                            onChange={e => setVal(product.id, col, e.target.value)}
                            onKeyDown={e => handleKeyDown(e, rowIdx, editableColIndex(col))}
                            className={`w-24 px-2 py-1 text-sm border rounded transition-colors
                              focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400
                              ${dirty[product.id]?.price !== undefined ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}
                          />
                        )}
                        {col === 'cost' && (
                          <input
                            id={`cell-${rowIdx}-${col}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={String(getVal(product, col) ?? '')}
                            onChange={e => setVal(product.id, col, e.target.value === '' ? null : e.target.value)}
                            onKeyDown={e => handleKeyDown(e, rowIdx, editableColIndex(col))}
                            className={`w-24 px-2 py-1 text-sm border rounded transition-colors
                              focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400
                              ${dirty[product.id]?.cost !== undefined ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}
                          />
                        )}
                        {col === 'supplier_id' && (
                          <select
                            value={(getVal(product, col) as string) ?? ''}
                            onChange={e => setVal(product.id, col, e.target.value || null)}
                            className={`w-36 px-2 py-1 text-sm border rounded transition-colors
                              focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400
                              ${dirty[product.id]?.supplier_id !== undefined ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}
                          >
                            <option value="">—</option>
                            {suppliers.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        )}
                        {col === 'supplier_barcode' && (
                          <input
                            id={`cell-${rowIdx}-${col}`}
                            type="text"
                            value={(getVal(product, col) as string) ?? ''}
                            onChange={e => setVal(product.id, col, e.target.value)}
                            onKeyDown={e => handleKeyDown(e, rowIdx, editableColIndex(col))}
                            className={`w-32 px-2 py-1 text-sm border rounded transition-colors
                              focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400
                              ${dirty[product.id]?.supplier_barcode !== undefined ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}
                          />
                        )}
                        {col === 'is_active' && (
                          <button
                            onClick={() => setVal(product.id, col, !(getVal(product, col) as boolean))}
                            className="flex items-center gap-1"
                          >
                            {getVal(product, col) ? (
                              <ToggleRight className="w-6 h-6 text-green-500" />
                            ) : (
                              <ToggleLeft className="w-6 h-6 text-gray-400" />
                            )}
                          </button>
                        )}
                        {col === 'description' && (
                          <input
                            id={`cell-${rowIdx}-${col}`}
                            type="text"
                            value={(getVal(product, col) as string) ?? ''}
                            onChange={e => setVal(product.id, col, e.target.value)}
                            onKeyDown={e => handleKeyDown(e, rowIdx, editableColIndex(col))}
                            className={`w-48 px-2 py-1 text-sm border rounded transition-colors
                              focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400
                              ${dirty[product.id]?.description !== undefined ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Floating Save Bar */}
      {dirtyCount > 0 && (
        <div className="sticky bottom-0 mt-4 bg-white border border-gray-200 rounded-lg shadow-lg px-6 py-3
                        flex items-center justify-between z-10">
          <div className="text-sm text-gray-700">
            <span className="font-semibold text-amber-600">{dirtyCount}</span> מוצרים שונו
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={discardAll}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700
                         bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors
                         disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              בטל שינויים
            </button>
            <button
              onClick={saveAll}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white
                         bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors
                         disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              שמור הכל
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
